import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TicketsAPI } from "../../../api/tickets";
import { AuthAPI } from "../../../api/auth";
import { StatusAPI } from "../../../api/statuses";
import api from "../../../api/api";

import {
  DepartmentsAPI,
  CategoriesAPI,
  SubcategoriesAPI,
  UsersAPI,
  SchoolsAPI,
} from "../../../api/lookups";

import TicketTypePicker from "../../../components/helpdesk/TicketTypePicker";
import TicketLifecyclePanel from "../../../components/helpdesk/TicketLifecyclePanel";
import TicketMessageForm from "../../../components/TicketMessageForm";
import SearchableUserSelect from "../../../components/SearchableUserSelect";
import SearchableMultiUserSelect from "../../../components/SearchableMultiUserSelect";
import MessageHtml from "../../../components/MessageHtml";
import InternalNoteViewer from "../../../components/tiptap/InternalNoteViewer";
import InternalNoteEditor from "../../../components/tiptap/InternalNoteEditor";
import ScheduleTicketModal from "../../../components/ScheduleTicketModal";
import RunRecipeModal from "../../../components/RunRecipeModal";
import { HelpdeskRecipesAPI } from "../../../api/helpdeskRecipes";

export default function TicketDetail({ createMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: { type: "doc", content: [] },
    requester_id: null,
    status_id: null,
    priority_id: null,
    department_id: null,
    school_id: null,
    category_id: null,
    subcategory_id: null,
    assignee_ids: [],
    follower_ids: [],
  });

  const [statuses, setStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [allowedSchools, setAllowedSchools] = useState([]);

  const [showProblemModal, setShowProblemModal] = useState(false);
  const [problems, setProblems] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [notifyUsers, setNotifyUsers] = useState(true);

  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveName, setResolveName] = useState("");
  const [resolveEmail, setResolveEmail] = useState("");
  const [resolvingUser, setResolvingUser] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [schools, setSchools] = useState([]);
  const [resolveSchoolIds, setResolveSchoolIds] = useState([]);
  const [resolveDefaultSchoolId, setResolveDefaultSchoolId] = useState("");

  const [kbArticles, setKbArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [otherRaisedTickets, setOtherRaisedTickets] = useState([]);

  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [recipeNotice, setRecipeNotice] = useState("");

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------

const getNameById = (list, id) => {
  if (!id) return "-";
  const item = list.find((i) => String(i.id) === String(id));
  return item?.name || "-";
};

const normalizeTipTapValue = (value) => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.type === "doc") return parsed;
    } catch {}
  }

  if (!value) return { type: "doc", content: [] };

  // Already parsed TipTap JSON
  if (typeof value === "object") return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.type === "doc") return parsed;
    } catch {
      // not JSON
    }
    return { type: "doc", content: [] };
  }

  return { type: "doc", content: [] };
};

const buildFormFromTicket = (t) => ({
  title: t.title,
  description: normalizeTipTapValue(t.description),
  requester_id: t.requester_user_id || t.requester_id || null,
  status_id: t.status_id,
  priority_id: t.priority_id || null,
  department_id: t.department_id,
  school_id: t.school_id,
  category_id: t.category_id,
  subcategory_id: t.subcategory_id,
  assignee_ids: t.assignees?.map((a) => a.id) || [],
  follower_ids: t.followers?.map((f) => f.id).filter(Boolean) || [],
});

const handleSchedule = async (data) => {
  try {
    const res = await fetch(`/api/tickets/${ticket.id}/schedule`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("lumat_token")}`,
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) throw new Error("Request failed");

    setShowScheduleModal(false);
    window.location.reload();
  } catch (e) {
    console.error(e);
    alert("Failed to schedule ticket");
  }
};

const handleCancelSchedule = async () => {
  if (!window.confirm("Cancel this schedule?")) return;

  try {
    const res = await fetch(`/api/tickets/${ticket.id}/schedule`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("lumat_token")}`,
      },
    });

    if (!res.ok) throw new Error("Request failed");

    window.location.reload();
  } catch (e) {
    console.error(e);
    alert("Failed to cancel schedule");
  }
};

async function loadKbArticles(ticketId) {
  try {
    const data = await api(`/helpdesk/kb/ticket/${ticketId}`);
    setKbArticles(data.data || []);
  } catch (err) {
    console.error("Failed to load KB articles", err);
    setKbArticles([]);
  }
}

function extractPlainTextFromTipTap(node) {
  if (!node) return "";

  if (Array.isArray(node)) {
    return node.map(extractPlainTextFromTipTap).join(" ");
  }

  if (node.type === "text") {
    return node.text || "";
  }

  if (Array.isArray(node.content)) {
    return node.content.map(extractPlainTextFromTipTap).join(" ");
  }

  return "";
}

function getArticleOverview(articleBody, maxLength = 220) {
  const fullText = extractPlainTextFromTipTap(articleBody).replace(/\s+/g, " ").trim();

  if (!fullText) return "Open the article for full guidance.";

  if (fullText.length <= maxLength) return fullText;

  return `${fullText.slice(0, maxLength).trim()}...`;
}

async function sendArticleToUser(article) {
  try {
    const articleUrl = `${window.location.origin}/helpdesk/kb/${article.id}`;
    const overview = getArticleOverview(article.body);

    await TicketsAPI.addMessage(ticket.id, {
      body: JSON.stringify({
        type: "doc",
        content: [
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: `Knowledge Base: ${article.title}`,
                marks: [{ type: "bold" }],
              },
            ],
          },
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: overview,
              },
            ],
          },
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: "Open article",
                marks: [
                  {
                    type: "link",
                    attrs: {
                      href: articleUrl,
                      target: "_blank",
                    },
                  },
                ],
              },
            ],
          },
        ],
      }),
      visibility: "public",
    });

    await fetchMessages();
  } catch (err) {
    console.error("Failed to send article to user", err);
  }
}

async function addArticleToInternal(article) {
  try {
    await TicketsAPI.addMessage(ticket.id, {
      body: JSON.stringify({
        type: "doc",
        content: [
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: `KB Article: ${article.title}`,
              },
            ],
          },
        ],
      }),
      visibility: "internal",
    });

    await fetchMessages();
  } catch (err) {
    console.error("Failed to add internal KB note", err);
  }
}

  const fetchMessages = async () => {
    try {
      const msgs = await TicketsAPI.listMessages(id);
      setMessages(
        msgs.map((m) => ({
          ...m,
          body: normalizeTipTapValue(m.body),
        }))
      );
    } catch (err) {
      setError(err.message);
    }
  };

  // ------------------------------------------------------------
  // Initial load
  // ------------------------------------------------------------

    useEffect(() => {
      async function load() {
        try {
          // CREATE MODE
          if (createMode) {
            const [
              user,
              st,
              deps,
              staffList,
              schoolPerms,
              allSchools,
            ] = await Promise.all([
              AuthAPI.me(),
              StatusAPI.list(),
              DepartmentsAPI.list(),
              UsersAPI.list(),
              TicketsAPI.allowedSchools("tickets.create"),
              SchoolsAPI.list(),
            ]);

            setCurrentUser(user);
            setStatuses(st);
            setDepartments(deps);
            setUsers(staffList);
            setAllowedSchools(Array.isArray(schoolPerms) ? schoolPerms : []);
            setSchools(Array.isArray(allSchools) ? allSchools : []);

            const defaultStatus = st.find(
              (s) => s.name.toLowerCase() !== "scheduled"
            );

            setForm((f) => ({
              ...f,
              requester_id: user?.id || null,
              status_id: defaultStatus?.id || null,
            }));

            setEditMode(true);
            return;
          }

          // EXISTING TICKET MODE
          const [
            t,
            msgs,
            user,
            st,
            deps,
            staffList,
            schoolPerms,
            allSchools,
            relatedRaised,
          ] = await Promise.all([
            TicketsAPI.get(id),
            TicketsAPI.listMessages(id),
            AuthAPI.me(),
            StatusAPI.list(),
            DepartmentsAPI.list(),
            UsersAPI.list(),
            TicketsAPI.allowedSchools("tickets.edit"),
            SchoolsAPI.list(),
            TicketsAPI.listOtherRaised(id),
          ]);

          setTicket({
            ...t,
            description: normalizeTipTapValue(t.description),
          });

          setFollowers(Array.isArray(t.followers) ? t.followers : []);

          setMessages(
            msgs.map((m) => ({
              ...m,
              body: normalizeTipTapValue(m.body),
            }))
          );

          setCurrentUser(user);
          setStatuses(st);
          setDepartments(deps);
          setUsers(staffList);
          setAllowedSchools(Array.isArray(schoolPerms) ? schoolPerms : []);
          setSchools(Array.isArray(allSchools) ? allSchools : []);
          setOtherRaisedTickets(
            Array.isArray(relatedRaised) ? relatedRaised : []
          );

          setForm(buildFormFromTicket(t));

          await loadKbArticles(t.id);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }

      load();
    }, [id, createMode]);

    useEffect(() => {
      async function loadAssignableUsers() {
        if (!form.school_id || !form.department_id) {
          setAssignableUsers([]);
          return;
        }

        try {
          const result = await TicketsAPI.assignableUsers(
            form.school_id,
            form.department_id
          );

          setAssignableUsers(Array.isArray(result) ? result : []);
        } catch (err) {
          console.error("Failed to load assignable users:", err);
          setAssignableUsers([]);
        }
      }

      loadAssignableUsers();
    }, [form.school_id, form.department_id]);

    useEffect(() => {
      if (!form.school_id || !form.department_id) {
        return;
      }

      const allowedIds = new Set(
        assignableUsers.map((user) => String(user.id))
      );

      setForm((current) => {
        const filteredAssignees = (current.assignee_ids || []).filter((id) =>
          allowedIds.has(String(id))
        );

        if (
          filteredAssignees.length === (current.assignee_ids || []).length
        ) {
          return current;
        }

        return {
          ...current,
          assignee_ids: filteredAssignees,
        };
      });
    }, [assignableUsers, form.school_id, form.department_id]);

    useEffect(() => {
      if (createMode || !id) return;

      const interval = setInterval(async () => {
        try {
          const [t, msgs, relatedRaised] = await Promise.all([
            TicketsAPI.get(id),
            TicketsAPI.listMessages(id),
            TicketsAPI.listOtherRaised(id),
          ]);

          setTicket({
            ...t,
            description: normalizeTipTapValue(t.description),
          });

          setFollowers(Array.isArray(t.followers) ? t.followers : []);

          setMessages(
            msgs.map((m) => ({
              ...m,
              body: normalizeTipTapValue(m.body),
            }))
          );

          setOtherRaisedTickets(Array.isArray(relatedRaised) ? relatedRaised : []);
        } catch (err) {
          console.error("Ticket auto-refresh failed", err);
        }
      }, 30000);

      return () => clearInterval(interval);
    }, [id, createMode]);

    useEffect(() => {
        if (showProblemModal) {
          import("../../../api/problems").then(({ ProblemsAPI }) => {
            ProblemsAPI.list().then(setProblems);
          });
        }
      }, [showProblemModal]);

    useEffect(() => {
    if (!form.department_id) {
      setCategories([]);
      setSubcategories([]);
      setForm((f) => ({
        ...f,
        category_id: null,
        subcategory_id: null,
      }));
      return;
    }

    CategoriesAPI.list(form.department_id)
      .then((cats) => {
        setCategories(cats);

        setForm((f) => {
          const categoryStillValid = cats.some(
            (c) => String(c.id) === String(f.category_id)
          );

          if (categoryStillValid) return f;

          return {
            ...f,
            category_id: null,
            subcategory_id: null,
          };
        });
      })
      .catch((err) => setError(err.message));
  }, [form.department_id]);

  useEffect(() => {
    if (!form.category_id) {
      setSubcategories([]);
      setForm((f) => ({ ...f, subcategory_id: null }));
      return;
    }

    SubcategoriesAPI.list(form.category_id)
      .then((subs) => {
        setSubcategories(subs);

        setForm((f) => {
          const subcategoryStillValid = subs.some(
            (sc) => String(sc.id) === String(f.subcategory_id)
          );

          if (subcategoryStillValid) return f;

          return {
            ...f,
            subcategory_id: null,
          };
        });
      })
      .catch((err) => setError(err.message));
  }, [form.category_id]);

  const refreshAfterRecipe = async () => {
    const [fresh, msgs] = await Promise.all([TicketsAPI.get(ticket.id), TicketsAPI.listMessages(ticket.id)]);
    setTicket({ ...fresh, description: normalizeTipTapValue(fresh.description) });
    setFollowers(Array.isArray(fresh.followers) ? fresh.followers : []);
    setMessages(msgs.map((m) => ({ ...m, body: normalizeTipTapValue(m.body) })));
  };

  const runTicketRecipe = async (recipeId) => {
    await HelpdeskRecipesAPI.runForTicket(ticket.id, recipeId);
    setRecipeNotice("Recipe completed successfully.");
    try { await refreshAfterRecipe(); } catch (err) { console.error("Ticket refresh after recipe failed", err); }
  };

  const handleSave = async () => {
    try {
      const updateResult=await TicketsAPI.update(ticket.id, {
        ...form,
        description: JSON.stringify(form.description),
      });

      if(updateResult?.transferred){navigate("/helpdesk/tickets",{state:{ticketTransferred:true}});return;}
      const fresh = await TicketsAPI.get(ticket.id);

      setTicket({
        ...fresh,
        description: normalizeTipTapValue(fresh.description),
      });
      setFollowers(Array.isArray(fresh.followers) ? fresh.followers : []);

      setForm(buildFormFromTicket(fresh));

      await loadKbArticles(fresh.id);

      setEditMode(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreate = async () => {
  if (!form.title?.trim()) {
    setError("Please enter a ticket title.");
    return;
  }

  if (!form.school_id) {
    setError("Please select a school.");
    return;
  }

  try {
      setError(null);

      const newTicket = await TicketsAPI.create({
        title: form.title.trim(),
        ticket_type_id: form.ticket_type_id,
        description: JSON.stringify(form.description),
        requester_id: form.requester_id || currentUser?.id,
        priority_id: form.priority_id || "",
        status_id: form.status_id || "",
        department_id: form.department_id || "",
        category_id: form.category_id || "",
        subcategory_id: form.subcategory_id || "",
        school_id: form.school_id,
        assignee_ids: form.assignee_ids || [],
      });

      // Followers are currently handled by the update endpoint,
      // rather than the create endpoint.
      if (form.follower_ids?.length > 0) {
        await TicketsAPI.update(newTicket.id, {
          follower_ids: form.follower_ids,
        });
      }

      navigate(`/helpdesk/tickets/${newTicket.id}`);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <p>{createMode ? "Loading new ticket…" : "Loading ticket…"}</p>;
  }

  if (error && !createMode) {
    return <p className="text-red-500">Error: {error}</p>;
  }

  if (!createMode && !ticket) {
    return <p>Ticket not found</p>;
  }

  const sortNewestFirst = (items) =>
    [...items].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

  const publicMessages = sortNewestFirst(
    messages.filter((m) => m.visibility === "public")
  );

  const internalMessages = sortNewestFirst(
    messages.filter((m) => m.visibility === "internal")
  );

  const canEditSchoolOptions = allowedSchools.length > 0;

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <div className="flex flex-col gap-4 p-3 sm:p-4 xl:flex-row xl:h-[calc(100vh-80px)] bg-[var(--nj-bg)] text-[var(--nj-text)]">
      {/* LEFT COLUMN */}
      <div className="w-full min-w-0 break-words xl:w-1/4 border border-[var(--nj-border)] bg-[var(--nj-surface)] p-4 flex flex-col xl:overflow-hidden">
        <h2 className="text-lg font-semibold mb-4">
          {createMode ? "New Ticket" : ticket.title || "Ticket details"}
        </h2>
        {createMode && error && (
          <div className="mb-3 text-sm text-red-600">
            {error}
          </div>
        )}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            className="bg-gray-200 px-3 py-1 rounded"
            onClick={() => navigate(-1)}
          >
            Back to tickets
          </button>

          {createMode ? (
            <button
              className="bg-blue-500 text-white px-3 py-1 rounded"
              onClick={handleCreate}
            >
              Create Ticket
            </button>
          ) : editMode ? (
            <button
              className="bg-blue-500 text-white px-3 py-1 rounded"
              onClick={handleSave}
            >
              Save
            </button>
          ) : (
            <button
              className="bg-blue-500 text-white px-3 py-1 rounded"
              onClick={() => {
                setForm(buildFormFromTicket(ticket));
                setEditMode(true);
              }}
            >
              Edit
            </button>
          )}

        {!createMode && (
          <div className="relative">
            <button
              className="bg-gray-200 px-3 py-1 rounded"
              onClick={() => setShowActionsMenu((v) => !v)}
            >
              Actions
            </button>

            {showActionsMenu && (
              <div className="absolute left-0 bg-[var(--nj-surface)] text-[var(--nj-text)] border border-[var(--nj-border)] rounded shadow-md mt-1 z-10 min-w-[160px]">
                <button
                  type="button"
                  className="block w-full text-left px-3 py-2 hover:bg-[var(--nj-surface-secondary)]"
                  onClick={() => { setShowRecipeModal(true); setShowActionsMenu(false); setRecipeNotice(""); }}
                >
                  Run Recipe
                </button>
                <button
                  className="block w-full text-left px-3 py-2 hover:bg-gray-100"
                  onClick={() => {
                    setShowScheduleModal(true);
                    setShowActionsMenu(false);
                  }}
                >
                  Schedule
                </button>

                {ticket.active_schedule_id && (
                  <button
                    className="block w-full text-left px-3 py-2 hover:bg-gray-100 text-red-600"
                    onClick={() => {
                      handleCancelSchedule();
                      setShowActionsMenu(false);
                    }}
                  >
                    Cancel Schedule
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        </div>

        {recipeNotice && <div role="status" className="mb-2 rounded border border-[var(--nj-border)] p-2 text-sm">{recipeNotice}</div>}
        <div className="min-w-0 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:pr-2 space-y-3 text-sm">
          {editMode ? (
            <>
              <div>
                <label className="font-semibold block mb-1">Status</label>
                <select
                  className="nj-ticket-control border p-2 w-full"
                  value={form.status_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">-- Select Status --</option>
                    {statuses
                      .filter((s) => s.name.toLowerCase() !== "scheduled")
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                </select>
              </div>

              <div>
                <label className="font-semibold block mb-1">Priority</label>
                <select
                  className="nj-ticket-control border p-2 w-full"
                  value={form.priority_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      priority_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">-- Select Priority --</option>
                  <option value="1">Low</option>
                  <option value="2">Medium</option>
                  <option value="3">High</option>
                </select>
              </div>

              <div>
                <label className="font-semibold block mb-1">Raised by</label>
                <SearchableUserSelect
                  users={users}
                  value={form.requester_id}
                  onChange={(userId) =>
                    setForm({
                      ...form,
                      requester_id: userId,
                    })
                  }
                  placeholder="Search for a user..."
                />
              </div>

              {!form.department_id && (
                <div role="alert" className="mb-3 rounded border border-[var(--nj-border)] bg-[var(--nj-surface-secondary)] p-3 text-[var(--nj-text)]">
                  <strong className="block">Department required</strong>
                  Select a department to enable categories, subcategories and technician assignment.
                </div>
              )}
              <div>
                <label className="font-semibold block mb-1">Department</label>
                <select
                  className="nj-ticket-control border p-2 w-full"
                  value={form.department_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      department_id: e.target.value || null,
                    })
                  }
                >
                  <option value="">-- Select Department --</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-semibold block mb-1">School</label>
                <select
                  className="nj-ticket-control border p-2 w-full"
                  disabled={!canEditSchoolOptions}
                  value={form.school_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      school_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">
                    {canEditSchoolOptions
                      ? "-- Select School --"
                      : "No permitted schools"}
                  </option>
                  {allowedSchools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-semibold block mb-1">Category</label>
                <select
                  className="nj-ticket-control border p-2 w-full"
                  value={form.category_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      category_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-semibold block mb-1">Subcategory</label>
                <select
                  className="nj-ticket-control border p-2 w-full"
                  value={form.subcategory_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      subcategory_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">-- Select Subcategory --</option>
                  {subcategories.map((sc) => (
                    <option key={sc.id} value={sc.id}>
                      {sc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-semibold block mb-1">Assigned to</label>
                  <SearchableMultiUserSelect
                    users={assignableUsers}
                  value={form.assignee_ids || []}
                  onChange={(assigneeIds) =>
                    setForm({
                      ...form,
                      assignee_ids: assigneeIds,
                    })
                  }
                  placeholder="Search for technicians..."
                />
              </div>

              <div>
                <label className="font-semibold block mb-1">Followers</label>
                <SearchableMultiUserSelect
                  users={users}
                  value={form.follower_ids || []}
                  onChange={(followerIds) =>
                    setForm({
                      ...form,
                      follower_ids: followerIds,
                    })
                  }
                  placeholder="Search for followers..."
                />
              </div>

            {!createMode && (
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  className="bg-gray-300 px-3 py-1 rounded"
                  onClick={() => {
                    setForm(buildFormFromTicket(ticket));
                    setEditMode(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            </>
          ) : (
            <>
              <div>
                <div className="font-semibold">Status</div>
                <div>{getNameById(statuses, ticket.status_id)}</div>
              </div>

              {ticket.active_schedule_id && (
                <div>
                  <div className="font-semibold">Schedule</div>
                  <div className="space-y-1">
                    <div>
                      {ticket.schedule_start_at
                        ? new Date(ticket.schedule_start_at).toLocaleString("en-GB", ticket.lifecycle_v2_enabled ? { timeZone: "Europe/London" } : undefined)
                        : "-"}
                      {" → "}
                      {ticket.schedule_end_at
                        ? new Date(ticket.schedule_end_at).toLocaleString("en-GB", ticket.lifecycle_v2_enabled ? { timeZone: "Europe/London" } : undefined)
                        : "-"}
                    </div>

                    <div className="text-sm text-gray-600">
                      Duration: {ticket.schedule_duration_minutes || 0} minutes
                    </div>

                    <div className="text-sm text-gray-600">
                      Assigned:{" "}
                      {ticket.schedule_assignees?.length
                        ? ticket.schedule_assignees.map((u) => u.name).join(", ")
                        : "None"}
                    </div>

                    <div className="text-sm text-gray-600">
                      Notify requester: {ticket.schedule_notify_requester ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <div className="font-semibold">Raised by</div>
                <div>
                  {(() => {
                    const user = users.find(
                      (u) =>
                        String(u.id) ===
                        String(ticket.requester_user_id || ticket.requester_id)
                    );

                    if (user) {
                      return (
                        <>
                          {user.name}
                          {user.is_unresolved_email_user && (
                            <>
                              <span className="ml-2 text-xs text-orange-600">
                                (unknown user)
                              </span>

                              <button
                                className="ml-2 text-xs text-blue-600 underline"
                                onClick={() => {
                                  setResolvingUser(user);
                                  setResolveName(user.name || "");
                                  setResolveEmail(user.email || "");

                                  const userSchoolIds = Array.isArray(user.schools) ? user.schools : [];
                                  const initialDefaultSchoolId = userSchoolIds[0] || ticket.school_id || "";

                                  setResolveSchoolIds(
                                    userSchoolIds.length > 0
                                      ? userSchoolIds.map((s) => String(s))
                                      : ticket.school_id
                                        ? [String(ticket.school_id)]
                                        : []
                                  );

                                  setResolveDefaultSchoolId(
                                    initialDefaultSchoolId ? String(initialDefaultSchoolId) : ""
                                  );

                                  setShowResolveModal(true);
                                }}
                              >
                                Confirm user details
                              </button>
                            </>
                          )}
                        </>
                      );
                    }

                    return ticket.requester_name || "Unknown";
                  })()}
                </div>
              </div>

              <div>
                <div className="font-semibold">Assigned to</div>
                <div>
                  {ticket.assignees?.map((a) => a.name).join(", ") ||
                    "Unassigned"}
                </div>
              </div>

              <div>
                <div className="font-semibold">Followers</div>

                {followers.length > 0 ? (
                  <div className="space-y-1">
                    {followers.map((f) => (
                      <div key={f.id || f.email}>
                        <span>{f.name || f.email}</span>

                        {f.is_unresolved_email_user && (
                          <>
                            <span className="ml-2 text-xs text-orange-600">
                              (unknown user)
                            </span>

                            <button
                              className="ml-2 text-xs text-blue-600 underline"
                              onClick={() => {
                                setResolvingUser(f);
                                setResolveName(f.name || "");
                                setResolveEmail(f.email || "");

                                const userSchoolIds = Array.isArray(f.schools)
                                  ? f.schools
                                  : [];

                                const initialDefaultSchoolId =
                                  userSchoolIds[0] || ticket.school_id || "";

                                setResolveSchoolIds(
                                  userSchoolIds.length > 0
                                    ? userSchoolIds.map((s) => String(s))
                                    : ticket.school_id
                                      ? [String(ticket.school_id)]
                                      : []
                                );

                                setResolveDefaultSchoolId(
                                  initialDefaultSchoolId
                                    ? String(initialDefaultSchoolId)
                                    : ""
                                );

                                setShowResolveModal(true);
                              }}
                            >
                              Confirm user details
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>No followers</div>
                )}
              </div>

              {!ticket.department_id && (
                <div role="alert" className="mb-3 rounded border border-[var(--nj-border)] bg-[var(--nj-surface-secondary)] p-3">
                  <strong className="block">Department not set</strong>
                  Edit this ticket and select a department to enable categories, subcategories and technician assignment.
                </div>
              )}
              <div>
                <div className="font-semibold">Department</div>
                <div>{getNameById(departments, ticket.department_id)}</div>
              </div>

              <div>
                <div className="font-semibold">School</div>
                <div>{getNameById(allowedSchools, ticket.school_id)}</div>
              </div>

              <div>
                <div className="font-semibold">Category</div>
                <div>{getNameById(categories, ticket.category_id)}</div>
              </div>

              <div>
                <div className="font-semibold">Subcategory</div>
                <div>{getNameById(subcategories, ticket.subcategory_id)}</div>
              </div>

              <div>
                <div className="font-semibold">Problem</div>
                {ticket.problem_id ? (
                  <div>
                    <Link
                      to={`/helpdesk/problems/${ticket.problem_id}`}
                      className="text-blue-600 underline"
                    >
                      {ticket.problem_title || `Problem #${ticket.problem_id}`}
                    </Link>
                    <div className="text-sm text-gray-500">
                      {ticket.problem_status || "Linked"}
                    </div>
                  </div>
                ) : (
                  <div>-</div>
                )}
              </div>

              <div className="mt-4 border border-[var(--nj-border)] rounded p-3 bg-[var(--nj-surface-secondary)]">
                <div className="font-semibold mb-2">Other tickets raised</div>

                {otherRaisedTickets.length === 0 ? (
                  <div className="text-sm text-gray-500">No other tickets</div>
                ) : (
                  <div className="space-y-2">
                    {otherRaisedTickets.map((t) => (
                      <div key={t.id} className="text-sm">
                        <Link
                          to={`/helpdesk/tickets/${t.id}`}
                          className="text-blue-600 underline"
                        >
                          #{t.id} — {t.title}
                        </Link>
                        <div className="text-xs text-gray-500">
                          {t.status_name || "-"} •{" "}
                          {t.created_at ? new Date(t.created_at).toLocaleString() : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {showResolveModal && resolvingUser && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-3">
              <div className="bg-[var(--nj-surface)] text-[var(--nj-text)] p-4 rounded w-full max-w-[400px] max-h-[90vh] overflow-y-auto">
                <h2 className="text-lg font-semibold mb-3">Resolve user</h2>

                <input
                  className="nj-ticket-control border p-2 w-full mb-2"
                  value={resolveName}
                  onChange={(e) => setResolveName(e.target.value)}
                  placeholder="Name"
                />

                <input
                  className="nj-ticket-control border p-2 w-full mb-3"
                  value={resolveEmail}
                  onChange={(e) => setResolveEmail(e.target.value)}
                  placeholder="Email"
                />

                  <div className="border rounded p-2 mb-2 max-h-40 overflow-y-auto">
                    <div className="text-sm font-medium mb-2">Schools</div>

                    {schools.length === 0 ? (
                      <div className="text-sm text-gray-500">No schools available</div>
                    ) : (
                      <div className="space-y-2">
                        {schools.map((school) => {
                          const checked = resolveSchoolIds.includes(String(school.id));

                          return (
                            <label
                              key={school.id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setResolveSchoolIds((prev) => [
                                      ...prev,
                                      String(school.id),
                                    ]);

                                    setResolveDefaultSchoolId((prev) =>
                                      prev || String(school.id)
                                    );
                                  } else {
                                    setResolveSchoolIds((prev) =>
                                      prev.filter((id) => id !== String(school.id))
                                    );

                                    setResolveDefaultSchoolId((prev) =>
                                      prev === String(school.id) ? "" : prev
                                    );
                                  }
                                }}
                              />
                              <span>{school.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <select
                    className="nj-ticket-control border p-2 w-full mb-3"
                    value={resolveDefaultSchoolId}
                    onChange={(e) => setResolveDefaultSchoolId(e.target.value)}
                  >
                    <option value="">Select default school</option>
                    {schools
                      .filter((school) => resolveSchoolIds.includes(String(school.id)))
                      .map((school) => (
                        <option key={school.id} value={String(school.id)}>
                          {school.name}
                        </option>
                      ))}
                  </select>

                <div className="flex justify-end gap-2">
                  <button
                    className="px-3 py-1 bg-gray-300 rounded"
                    onClick={() => {
                      setShowResolveModal(false);
                      setResolvingUser(null);
                      setResolveName("");
                      setResolveEmail("");
                      setResolveSchoolIds([]);
                      setResolveDefaultSchoolId("");
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="px-3 py-1 bg-blue-600 text-white rounded"
                    onClick={async () => {
                      const { AdminUsersAPI } = await import("../../../api/adminUsers");

                    await AdminUsersAPI.update(resolvingUser.id, {
                      name: resolveName,
                      email: resolveEmail,
                      schoolIds: resolveSchoolIds.map((id) => Number(id)),
                      defaultSchoolId: resolveDefaultSchoolId
                        ? Number(resolveDefaultSchoolId)
                        : null,
                    });

                      setShowResolveModal(false);
                      setResolvingUser(null);
                      setResolveName("");
                      setResolveEmail("");
                      setResolveSchoolIds([]);
                      setResolveDefaultSchoolId("");

                      // refresh ticket + users
                      const fresh = await TicketsAPI.get(ticket.id);
                      const staffList = await UsersAPI.list();

                      setTicket({
                        ...fresh,
                        description: normalizeTipTapValue(fresh.description),
                      });
                      setFollowers(Array.isArray(fresh.followers) ? fresh.followers : []);
                      setUsers(staffList);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MIDDLE COLUMN */}
      <div className="w-full min-w-0 break-words xl:flex-1 border border-[var(--nj-border)] bg-[var(--nj-surface)] p-4 flex flex-col xl:overflow-hidden">
        {editMode && (
          <div className="mb-4">
            <label className="text-lg font-semibold block mb-2">
              Title
            </label>
            <input
              type="text"
              className="nj-ticket-control border p-2 w-full"
              value={form.title || ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  title: e.target.value,
                })
              }
              placeholder="Enter ticket title..."
            />
          </div>
        )}

        {!createMode && <TicketLifecyclePanel ticket={ticket} auditOnly={!ticket.lifecycle_v2_enabled} disabled={editMode} onUpdated={refreshAfterRecipe} />}
        {createMode && <TicketTypePicker departmentId={form.department_id} value={form.ticket_type_id} onChange={(ticket_type_id,department_id)=>setForm(f=>({...f,ticket_type_id,...(department_id?{department_id}:{})}))} />}
        <h2 className="text-lg font-semibold mb-4">Description</h2>

        {editMode ? (
          <InternalNoteEditor
            value={form.description}
            onChange={(val) =>
              setForm((f) => ({ ...f, description: val }))
            }
          />
        ) : (
          <InternalNoteViewer value={ticket.description} />
        )}
  
        {!createMode && (
        <>
          <h3 className="text-lg font-semibold mt-6 mb-2">
            Communication with user
          </h3>

          <div className="min-w-0 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:pr-2">
            <ul className="space-y-2">
              {publicMessages.map((m) => (
                <li key={m.id} className="border border-[var(--nj-border)] rounded p-2 bg-[var(--nj-surface-secondary)]">
                  <MessageHtml value={m.body} />

                  {Array.isArray(m.attachments) &&
                    m.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.attachments.map((a) => (
                          <div key={a.id}>
                            <a
                              href={a.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline text-sm"
                            >
                              {a.filename}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}

                  <small className="text-gray-500 block mt-2">
                    {m.author_name || "Unknown"} —{" "}
                    {new Date(m.created_at).toLocaleString()}
                  </small>
                </li>
              ))}
            </ul>
          </div>

          {currentUser && (
            <TicketMessageForm
              ticketId={ticket.id}
              authorId={currentUser.id}
              visibility="public"
              triage={ticket.lifecycle_v2_enabled && ticket.is_triage && String(currentUser.id)!==String(ticket.requester_user_id??ticket.requester_id)}
              onMessageAdded={fetchMessages}
              onRecipeCompleted={refreshAfterRecipe}
            />
          )}
        </>
      )}
      </div>

      {/* RIGHT COLUMN */}
      <div className="w-full min-w-0 break-words xl:w-1/4 border border-[var(--nj-border)] bg-[var(--nj-surface)] p-4 flex flex-col xl:overflow-hidden">
      {!createMode && (
        <>
        <div style={{ marginBottom: 20 }}>
          <h3 className="text-md font-semibold mb-2">Knowledge Base</h3>

          {kbArticles.length === 0 && (
            <p className="text-sm text-gray-500">No relevant articles</p>
          )}

          {kbArticles.map((article) => (
            <div
              key={article.id}
              className="border border-[var(--nj-border)] rounded p-2 mb-2 bg-[var(--nj-surface-secondary)]"
            >
              <div className="font-medium">{article.title}</div>

              <div className="mt-2 flex gap-2 flex-wrap">
                {article.audience_type === "end_user" && (
                  <button
                    className="text-xs bg-blue-500 text-white px-2 py-1 rounded"
                    onClick={() => sendArticleToUser(article)}
                  >
                    Send to user
                  </button>
                )}

                <button
                  className="text-xs text-blue-600 underline"
                  onClick={() => setSelectedArticle(article)}
                >
                  View
                </button>
              </div>

              <div className="text-xs text-gray-500 mt-1">
                {article.audience_type === "end_user"
                  ? "End user"
                  : "Technician"}
              </div>
            </div>
          ))}
        </div>

        {/* ✅ MODAL (MOVED OUTSIDE MAP) */}
        {selectedArticle && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-3">
            <div className="bg-[var(--nj-surface)] text-[var(--nj-text)] p-4 rounded w-full min-w-0 max-w-[600px] max-h-[90vh] overflow-y-auto flex flex-col">
              <h2 className="text-lg font-semibold mb-2">
                {selectedArticle.title}
              </h2>

              <div className="text-xs text-gray-500 mb-2">
                {selectedArticle.audience_type === "end_user"
                  ? "End user"
                  : "Technician"}
              </div>

              <div className="min-h-0 min-w-0 flex-1 overflow-auto border rounded p-2">
                {selectedArticle.body ? (
                  <InternalNoteViewer value={selectedArticle.body} />
                ) : (
                  <p className="text-sm text-gray-500">No content</p>
                )}
              </div>

              <div className="flex justify-end mt-2">
                <button
                  className="px-3 py-1 bg-gray-300 rounded"
                  onClick={() => setSelectedArticle(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}  
    <h2 className="text-lg font-semibold mb-4">Internal communications</h2>

        <div className="min-w-0 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:pr-2">
          <ul className="space-y-2">
            {internalMessages.map((m) => (
              <li key={m.id} className="nj-internal-message border rounded p-2">
                <InternalNoteViewer value={m.body} />
                <small className="text-gray-500 block mt-2">
                  {m.author_name || "Unknown"} —{" "}
                  {new Date(m.created_at).toLocaleString()}
                </small>
              </li>
            ))}
          </ul>
        </div>

      {currentUser && (
        <TicketMessageForm
          ticketId={ticket.id}
          authorId={currentUser.id}
          visibility="internal"
          users={users}
          onMessageAdded={fetchMessages}
          onRecipeCompleted={refreshAfterRecipe}
        />
      )}
        </>
      )}
      </div>

      {!createMode && showProblemModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-3">
          <div className="bg-[var(--nj-surface)] text-[var(--nj-text)] p-4 rounded w-full min-w-0 max-w-[500px] max-h-[90vh] overflow-y-auto flex flex-col">
            <h2 className="text-lg font-semibold mb-2">Link to Problem</h2>

            <input
              type="text"
              placeholder="Search problems..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="nj-ticket-control border p-2 mb-2"
            />

            <div className="min-h-0 min-w-0 flex-1 overflow-auto border rounded mb-2">
              {problems
                .filter((p) =>
                  p.title.toLowerCase().includes(search.toLowerCase())
                )
                .map((p) => (
                  <div
                    key={p.id}
                    className={`p-2 cursor-pointer ${
                      selectedProblemId === p.id ? "bg-blue-100" : ""
                    }`}
                    onClick={() => setSelectedProblemId(p.id)}
                  >
                    <div className="font-semibold">{p.title}</div>
                    <div className="text-xs text-gray-500">{p.status}</div>
                  </div>
                ))}
            </div>

            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={notifyUsers}
                onChange={(e) => setNotifyUsers(e.target.checked)}
              />
              Notify end user
            </label>

            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 bg-gray-300 rounded"
                onClick={() => setShowProblemModal(false)}
              >
                Cancel
              </button>

              <button
                className="px-3 py-1 bg-blue-600 text-white rounded"
                disabled={!selectedProblemId}
                onClick={async () => {
                  const { ProblemsAPI } = await import("../../../api/problems");

                  await ProblemsAPI.linkTickets(selectedProblemId, {
                    ticket_ids: [ticket.id],
                    notify: notifyUsers,
                  });

                  setShowProblemModal(false);

                  const fresh = await TicketsAPI.get(ticket.id);
                  setTicket({
                    ...fresh,
                    description: normalizeTipTapValue(fresh.description),
                  });
                }}
              >
                Link
              </button>
            </div>
          </div>
        </div>
      )}

      {!createMode && (
        <RunRecipeModal
          open={showRecipeModal}
          ticketId={ticket.id}
          onClose={() => setShowRecipeModal(false)}
          onRun={runTicketRecipe}
        />
      )}
      {!createMode && showScheduleModal && (
        <ScheduleTicketModal
          open={showScheduleModal}
          ukTime={ticket.lifecycle_v2_enabled === true}
          onClose={() => setShowScheduleModal(false)}
          onSubmit={handleSchedule}
          users={users || []}
        />
      )}
    </div>
  );
}